import { action, makeObservable, observable } from 'mobx';
import {
  ApiError,
  ModelAuditLogService,
  TListModelAuditLogsRequest,
  TListModelAuditLogsStatesRequest,
  TUpdateModelAuditLogStateRequest,
} from '/src/api';
import BaseStore from '/src/context/stores/baseStore';
import RootStore from '/src/context/stores/rootStore';
import { ModelAuditLog, ModelAuditLogState, ModelName, TModelAuditLogId } from '/src/lib/models';
import { toastMessages } from '/src/lib/toast';
import { TResponseMetadata } from '/src/lib/types';

export default class ModelAuditLogStore extends BaseStore {
  isLoading = true;
  modelAuditLog?: ModelAuditLog;
  modelAuditLogs?: ModelAuditLog[];
  modelAuditLogsMeta?: TResponseMetadata;
  modelAuditLogStates?: ModelAuditLogState[] = [];
  selectedModelAuditLog?: ModelAuditLog;
  fieldAliases: Record<string, string> = {
    Fein: 'FEIN',
  };
  currentPage = 1;
  limit = 20;

  constructor(rootStore: RootStore) {
    super(rootStore);

    makeObservable(this, {
      isLoading: observable,
      modelAuditLog: observable,
      modelAuditLogs: observable,
      modelAuditLogsMeta: observable,
      modelAuditLogStates: observable,
      selectedModelAuditLog: observable,
      fieldAliases: observable,
      currentPage: observable,
      limit: observable,
      setIsLoading: action,
      setModelAuditLog: action,
      setModelAuditLogs: action,
      setModelAuditLogsMeta: action,
      setModelAuditLogStates: action,
      setSelectedModelAuditLog: action,
      setCurrentPage: action,
      setLimit: action,
      reset: action,
      fetchModelAuditLog: action,
      fetchModelAuditLogs: action,
      fetchModelAuditLogStates: action,
      updateModelAuditLogState: action,
    });
  }

  setIsLoading = (isLoading: boolean) => {
    this.isLoading = isLoading;
  };

  setModelAuditLog = (modelAuditLog?: ModelAuditLog) => {
    this.modelAuditLog = modelAuditLog;
  };

  setModelAuditLogs = (modelAuditLogs?: ModelAuditLog[]) => {
    this.modelAuditLogs = modelAuditLogs;
  };

  setModelAuditLogsMeta = (modelAuditLogsMeta?: TResponseMetadata) => {
    this.modelAuditLogsMeta = modelAuditLogsMeta;
  };

  setModelAuditLogStates = (modelAuditLogStates?: ModelAuditLogState[]) => {
    this.modelAuditLogStates = modelAuditLogStates;
  };

  setSelectedModelAuditLog = (modelAuditLog?: ModelAuditLog) => {
    this.selectedModelAuditLog = modelAuditLog;
  };

  setCurrentPage = (currentPage: number) => {
    this.currentPage = currentPage;
  };

  setLimit = (limit: number) => {
    this.limit = limit;
  };

  reset = () => {
    this.setModelAuditLogs(undefined);
    this.setModelAuditLogsMeta(undefined);
  };

  fetchModelAuditLog = async (id: TModelAuditLogId) => {
    if (!this.rootStore.userStore.isFuseAdmin) return;

    if (!this.modelAuditLogs) {
      await this.fetchModelAuditLogs();
    }
    const auditLog = (this.modelAuditLogs ?? []).find((log) => log.id === id);
    this.setModelAuditLog(auditLog);
  };

  fetchModelAuditLogs = async (req?: TListModelAuditLogsRequest) => {
    if (!this.rootStore.userStore.isFuseAdmin) {
      this.setIsLoading(false);
      this.setModelAuditLogs([]);
      return;
    }

    this.setIsLoading(true);
    try {
      const response = await ModelAuditLogService.list({ page: this.currentPage, limit: this.limit, ...req });
      const { meta, data } = response;
      this.setModelAuditLogs(
        data.filter((log) => log.model && Object.values(ModelName).includes(log.model_name as ModelName))
      );
      this.setModelAuditLogsMeta(meta);
    } catch (err) {
      this.rootStore.globalStore.handleApiError(err as ApiError, toastMessages.listAuditLogs.error);
    } finally {
      this.setIsLoading(false);
    }
  };

  fetchModelAuditLogStates = async (req?: TListModelAuditLogsStatesRequest) => {
    if (!this.rootStore.userStore.isFuseAdmin) return;
    try {
      const data = await ModelAuditLogService.listStates(req);
      this.setModelAuditLogStates(data);
      // this.setModelAuditLogStates(
      //   data?.length > 0 ? [defaultUnresolvedStatus, ...data] : [defaultUnresolvedStatus, defaultResolvedStatus]
      // );
    } catch (err) {
      this.rootStore.globalStore.handleApiError(err as ApiError, toastMessages.listAuditLogs.error);
    }
  };

  updateModelAuditLogState = async (req: TUpdateModelAuditLogStateRequest) => {
    if (!this.rootStore.userStore.isFuseAdmin) return;

    await ModelAuditLogService.updateState(req);
  };
}
